import { mergeDeep } from '@toggle/helpers';
import * as Types from 'echarts/types/dist/echarts';

import { getThemeColor } from '~/design-tokens/themes/theme-utils';
import { ThemeName } from '~/design-tokens/themes/themes';
import { fontFamily } from '~/design-tokens/typography/Fonts';

import { mergeSeriesLabel, mergeYAxis } from './utils';

export interface ApacheChartBase {
  theme?: ThemeName;
  onInit?: (refDom: HTMLDivElement) => void;
  isModal?: boolean;
}

export const DEFAULT_CHART_HEIGHT = 450;
export const DEFAULT_CHART_WIDTH = 700;
export const LEGEND_FONT_SIZE = 14;
export const LEGEND_BUFFER_COUNT = 1;
export const Y_AXIS_LABEL_WIDTH = 80;
export const Y_AXIS_PADDING = 10;
export const Y_AXIS_LABEL_PERCENTAGE = 0.1;

const DEFAULT_BACKGROUND_COLOR = 'transparent';

export interface SingleSeries {
  name: string;
  data: (number | null)[];
}

interface ApacheConfigArgs {
  theme: ThemeName;
  options: Types.EChartsOption;
  isModal?: boolean;
  yAxisLabelWidth?: number;
}

export const createApacheConfig = ({
  theme,
  options,
  isModal,
  yAxisLabelWidth,
}: ApacheConfigArgs): Types.EChartsOption => {
  const defaultAxisConfig = {
    axisLabel: {
      color: getThemeColor('--text-default', theme),
      fontSize: 12,
      overflow: 'break',
      width: Y_AXIS_LABEL_WIDTH,
    },
    axisTick: {
      lineStyle: {
        color: getThemeColor('--border-default', theme),
      },
    },
  };

  const defaultXAxisConfig = mergeDeep(defaultAxisConfig, {
    alignTicks: true,
    position: 'bottom',
    axisTick: {
      alignWithLabel: true,
    },
    axisLabel: {
      align: 'center',
    },
    splitLine: {
      show: false,
    },
    ...(isModal
      ? {}
      : {
          nameTruncate: {
            maxWidth: Y_AXIS_LABEL_WIDTH,
            ellipsis: '...',
          },
        }),
  });

  const defaultGridConfig = {
    top: '10%',
    bottom: '20%',
    left: '3%',
    right: '5%',
    containLabel: true,
  };

  const defaultTooltipConfig = {
    backgroundColor: getThemeColor('--surface-default', theme),
    borderColor: getThemeColor('--background-soft', theme),
    textStyle: {
      color: getThemeColor('--text-default', theme),
      fontSize: 12,
    },
    triggerOn: 'mousemove',
    padding: 12,
    extraCssText: 'box-shadow: var(--elevation-strong);',
  };

  const defaultLegendConfig = {
    type: 'scroll',
    bottom: options.title ? 20 : 15,
    right: 7,
    itemGap: 20,
    textStyle: {
      color: getThemeColor('--text-default', theme),
      fontSize: LEGEND_FONT_SIZE,
    },
    tooltip: {
      show: true,
    },
  };

  const seriesLabelConfig = {
    label: {
      color: getThemeColor('--text-default', theme),
    },
    endLabel: {
      color: getThemeColor('--text-default', theme),
    },
  };

  const xAxis = options.xAxis && mergeDeep(defaultXAxisConfig, options.xAxis);
  const yAxis =
    options.yAxis &&
    mergeYAxis({ theme, customYAxis: options.yAxis, isModal, yAxisLabelWidth });

  const baseGrid = isModal ? { containLabel: true } : defaultGridConfig;
  const grid = mergeDeep(baseGrid, options.grid ?? {});

  const axis = xAxis && yAxis ? { xAxis, yAxis } : {};
  const tooltip = mergeDeep(defaultTooltipConfig, options.tooltip ?? {});
  const legend = mergeDeep(defaultLegendConfig, options.legend ?? {});
  const processedSeries = mergeSeriesLabel({
    series: options.series,
    labelConfig: seriesLabelConfig,
  });

  return {
    ...options,
    backgroundColor: options.backgroundColor ?? DEFAULT_BACKGROUND_COLOR,
    textStyle: {
      fontFamily: fontFamily.proximaVara,
    },
    ...axis,
    grid,
    tooltip,
    legend,
    series: processedSeries,
    color: Array.from({ length: 12 }, (_, i) =>
      getThemeColor(`--chart-multi-lines-hex-${i + 1}`, theme)
    ),
  };
};
